// =====================================================
// Bulk Actions Component
// Actions for multiple selected transactions
// Agent 1: Review Queue UI
// =====================================================

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, ChevronDown, X, Percent, FileText, BookOpen } from 'lucide-react';
import { useBulkApproveTransactions, useBulkSetVATRate, useBulkAssignAccount, useBulkBookTransactions } from '@/hooks/useTransactionReview';

interface BulkActionsProps {
  selectedCount: number;
  selectedTransactionIds: string[];
  onClearSelection: () => void;
}

export function BulkActions({ selectedCount, selectedTransactionIds, onClearSelection }: BulkActionsProps) {
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showVATDialog, setShowVATDialog] = useState(false);
  const [showAccountDialog, setShowAccountDialog] = useState(false);
  const [selectedVATRate, setSelectedVATRate] = useState('8.1');
  const [debitAccount, setDebitAccount] = useState('');

  const bulkApprove = useBulkApproveTransactions();
  const bulkSetVAT = useBulkSetVATRate();
  const bulkAssignAccount = useBulkAssignAccount();
  const bulkBook = useBulkBookTransactions();

  const handleBulkApprove = async () => {
    try {
      await bulkApprove.mutateAsync(selectedTransactionIds);
      setShowApproveDialog(false);
      onClearSelection();
    } catch (error) {
      console.error('Bulk approve error:', error);
    }
  };

  const handleBulkSetVAT = async () => {
    try {
      await bulkSetVAT.mutateAsync({
        transactionIds: selectedTransactionIds,
        vatRate: parseFloat(selectedVATRate),
      });
      setShowVATDialog(false);
      onClearSelection();
    } catch (error) {
      console.error('Bulk set VAT error:', error);
    }
  };

  const handleBulkAssignAccount = async () => {
    if (!debitAccount.trim()) {
      return;
    }

    try {
      await bulkAssignAccount.mutateAsync({
        transactionIds: selectedTransactionIds,
        debitAccount: debitAccount.trim(),
      });
      setShowAccountDialog(false);
      setDebitAccount('');
      onClearSelection();
    } catch (error) {
      console.error('Bulk assign account error:', error);
    }
  };

  const handleBulkBook = async () => {
    try {
      await bulkBook.mutateAsync(selectedTransactionIds);
      onClearSelection();
    } catch (error) {
      console.error('Bulk booking error:', error);
    }
  };

  if (selectedCount === 0) {
    return null;
  }

  return (
    <>
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
        <div className="bg-brand-primary text-white rounded-full shadow-2xl px-6 py-4 flex items-center gap-4">
          {/* Selection Count */}
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="bg-white/20 text-white border-0 text-sm">
              {selectedCount}
            </Badge>
            <span className="text-sm font-medium">selected</span>
          </div>

          {/* Divider */}
          <div className="w-px h-6 bg-white/30" />

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Book All */}
            <Button
              size="sm"
              variant="secondary"
              className="bg-blue-600 hover:bg-blue-700 text-white border-0"
              onClick={handleBulkBook}
              disabled={bulkBook.isPending}
            >
              <BookOpen className="h-4 w-4 mr-1" />
              {bulkBook.isPending ? 'Booking...' : 'Book All'}
            </Button>

            {/* Approve All */}
            <Button
              size="sm"
              variant="secondary"
              className="bg-green-600 hover:bg-green-700 text-white border-0"
              onClick={() => setShowApproveDialog(true)}
              disabled={bulkApprove.isPending}
            >
              <Check className="h-4 w-4 mr-1" />
              Approve All
            </Button>

            {/* More Actions */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="secondary" className="bg-white/20 hover:bg-white/30 text-white border-0">
                  More Actions
                  <ChevronDown className="h-4 w-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Bulk Actions</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShowVATDialog(true)}>
                  <Percent className="h-4 w-4 mr-2" />
                  Set VAT Rate
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowAccountDialog(true)}>
                  <FileText className="h-4 w-4 mr-2" />
                  Assign Account
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Clear Selection */}
            <Button
              size="sm"
              variant="ghost"
              className="text-white hover:bg-white/20"
              onClick={onClearSelection}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Approve Confirmation Dialog */}
      <AlertDialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve {selectedCount} Transactions?</AlertDialogTitle>
            <AlertDialogDescription>
              This will approve all {selectedCount} selected transactions and mark them as reviewed. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkApprove}
              className="bg-green-600 hover:bg-green-700"
              disabled={bulkApprove.isPending}
            >
              {bulkApprove.isPending ? 'Approving...' : 'Approve All'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Set VAT Rate Dialog */}
      <AlertDialog open={showVATDialog} onOpenChange={setShowVATDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Set VAT Rate for {selectedCount} Transactions</AlertDialogTitle>
            <AlertDialogDescription>
              Choose the VAT rate to apply to all selected transactions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="vat-rate" className="text-sm font-medium">
              VAT Rate
            </Label>
            <select
              id="vat-rate"
              value={selectedVATRate}
              onChange={(e) => setSelectedVATRate(e.target.value)}
              className="w-full mt-2 px-3 py-2 border border-brand-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary"
            >
              <option value="0">0% (Export/Exempt)</option>
              <option value="2.6">2.6% (Reduced - Books, Newspapers)</option>
              <option value="3.8">3.8% (Special - Accommodation)</option>
              <option value="8.1">8.1% (Standard)</option>
            </select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkSetVAT} disabled={bulkSetVAT.isPending}>
              {bulkSetVAT.isPending ? 'Updating...' : 'Apply VAT Rate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Assign Account Dialog */}
      <AlertDialog open={showAccountDialog} onOpenChange={setShowAccountDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Assign Account to {selectedCount} Transactions</AlertDialogTitle>
            <AlertDialogDescription>
              Enter the debit account number to assign to all selected transactions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="debit-account" className="text-sm font-medium">
              Debit Account (Soll)
            </Label>
            <Input
              id="debit-account"
              type="text"
              placeholder="e.g., 6400"
              value={debitAccount}
              onChange={(e) => setDebitAccount(e.target.value)}
              className="mt-2"
              autoFocus
            />
            <p className="text-xs text-brand-text-secondary mt-1">
              Common accounts: 6400 (Services), 6420 (Meals), 6300 (Goods)
            </p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDebitAccount('')}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkAssignAccount}
              disabled={!debitAccount.trim() || bulkAssignAccount.isPending}
            >
              {bulkAssignAccount.isPending ? 'Assigning...' : 'Assign Account'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
